import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, Filter, Search, X, Edit2, Trash2, CheckSquare, Square, 
  LayoutGrid, List, Calendar, Clock, AlertCircle, ChevronDown, ChevronRight,
  GripVertical, User, Users, MoreHorizontal, Check,
  Globe, Briefcase, Handshake, MessageSquare
} from 'lucide-react';
import TaskComments, { CollaboratorsList } from '../components/TaskComments';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Task status configuration
const STATUSES = {
  'To Do': { color: 'bg-slate-100 text-slate-700 border-slate-200', dotColor: 'bg-slate-400' },
  'In Progress': { color: 'bg-blue-50 text-blue-700 border-blue-200', dotColor: 'bg-blue-500' },
  'On Hold': { color: 'bg-amber-50 text-amber-700 border-amber-200', dotColor: 'bg-amber-500' },
  'Completed': { color: 'bg-green-50 text-green-700 border-green-200', dotColor: 'bg-green-500' },
  'Cancelled': { color: 'bg-red-50 text-red-700 border-red-200', dotColor: 'bg-red-400' },
};

const PRIORITIES = {
  'Low': { color: 'bg-slate-100 text-slate-600', icon: null },
  'Medium': { color: 'bg-blue-100 text-blue-700', icon: null },
  'High': { color: 'bg-orange-100 text-orange-700', icon: AlertCircle },
  'Urgent': { color: 'bg-red-100 text-red-700', icon: AlertCircle },
};

const TASK_TYPES = {
  'self': { label: 'Personal', icon: User, color: 'text-blue-500' },
  'assigned': { label: 'Assigned', icon: User, color: 'text-green-500' },
  'collaboration': { label: 'Team', icon: Users, color: 'text-purple-500' },
};

// Kanban column statuses (subset for cleaner board)
const KANBAN_COLUMNS = ['To Do', 'In Progress', 'Completed'];

const Tasks = () => {
  const navigate = useNavigate();
  const { token, getAuthHeaders, currentOffice, user } = useAuth();
  
  // View state
  const [viewMode, setViewMode] = useState('kanban'); // 'kanban' | 'list'
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  
  // Dialog states
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  
  // New task form
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    task_type: 'self',
    assigned_to_id: '',
    collaborators: [],
    priority: 'Medium',
    due_date: '',
    category: '',
    tags: [],
  });
  
  // Team members for assignment
  const [teamMembers, setTeamMembers] = useState([]);
  
  // Checklist
  const [newChecklistItem, setNewChecklistItem] = useState('');
  
  // Drag state for kanban
  const [draggedTask, setDraggedTask] = useState(null);

  // Fetch tasks
  const fetchTasks = useCallback(async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/tasks/my-tasks`, {
        headers: getAuthHeaders(),
      });
      
      if (response.ok) {
        const myTasks = await response.json();
        
        // Also fetch collaboration tasks
        const collabResponse = await fetch(`${API_URL}/api/tasks/collaborations`, {
          headers: getAuthHeaders(),
        });
        
        let collabTasks = [];
        if (collabResponse.ok) {
          collabTasks = await collabResponse.json();
        }
        
        // Also fetch tasks I created for others
        const createdResponse = await fetch(`${API_URL}/api/tasks/created-by-me`, {
          headers: getAuthHeaders(),
        });
        
        let createdTasks = [];
        if (createdResponse.ok) {
          createdTasks = await createdResponse.json();
        }
        
        // Merge and deduplicate
        const allTasks = [...myTasks, ...collabTasks, ...createdTasks];
        const uniqueTasks = allTasks.reduce((acc, task) => {
          if (!acc.find(t => t.id === task.id)) {
            acc.push(task);
          }
          return acc;
        }, []);
        
        setTasks(uniqueTasks);
      } else {
        toast.error('Failed to load tasks');
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
      toast.error('Error loading tasks');
    } finally {
      setLoading(false);
    }
  }, [token, getAuthHeaders]);

  // Fetch team members for assignment
  const fetchTeamMembers = useCallback(async () => {
    if (!token) return;
    
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) {
        headers['X-Office-ID'] = currentOffice.id;
      }
      const response = await fetch(`${API_URL}/api/tasks/subordinates`, {
        headers,
      });
      
      if (response.ok) {
        const members = await response.json();
        setTeamMembers(members);
      }
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  }, [token, getAuthHeaders, currentOffice]);

  useEffect(() => {
    fetchTasks();
    fetchTeamMembers();
  }, [fetchTasks, fetchTeamMembers]);

  // Create task
  const handleCreateTask = async () => {
    if (!newTask.title.trim()) {
      toast.error('Please enter a task title');
      return;
    }
    
    try {
      const payload = {
        title: newTask.title,
        description: newTask.description || null,
        task_type: newTask.task_type,
        priority: newTask.priority,
        due_date: newTask.due_date || null,
        category: newTask.category || null,
        tags: newTask.tags,
      };
      
      if (newTask.task_type === 'assigned' && newTask.assigned_to_id) {
        payload.assigned_to_id = newTask.assigned_to_id;
      }
      
      if (newTask.task_type === 'collaboration' && newTask.collaborators.length > 0) {
        payload.collaborators = newTask.collaborators;
      }
      
      const response = await fetch(`${API_URL}/api/tasks/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      
      if (response.ok) {
        toast.success('Task created successfully');
        setIsCreateDialogOpen(false);
        resetNewTaskForm();
        fetchTasks();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create task');
      }
    } catch (error) {
      console.error('Error creating task:', error);
      toast.error('Error creating task');
    }
  };

  // Update task status
  const handleStatusChange = async (taskId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/tasks/${taskId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ status: newStatus }),
      });
      
      if (response.ok) {
        setTasks(prev => prev.map(t => 
          t.id === taskId ? { ...t, status: newStatus } : t
        ));
        if (selectedTask?.id === taskId) {
          setSelectedTask(prev => ({ ...prev, status: newStatus }));
        }
        toast.success('Status updated');
      } else {
        toast.error('Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Error updating status');
    }
  };

  // Update task
  const handleUpdateTask = async (taskId, updates) => {
    try {
      const response = await fetch(`${API_URL}/api/tasks/${taskId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(updates),
      });
      
      if (response.ok) {
        const updated = await response.json();
        setTasks(prev => prev.map(t => t.id === taskId ? updated : t));
        if (selectedTask?.id === taskId) {
          setSelectedTask(updated);
        }
        return true;
      } else {
        toast.error('Failed to update task');
        return false;
      }
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Error updating task');
      return false;
    }
  };

  // Delete task
  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/tasks/${taskId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      
      if (response.ok) {
        setTasks(prev => prev.filter(t => t.id !== taskId));
        setIsDetailDialogOpen(false);
        toast.success('Task deleted');
      } else {
        toast.error('Failed to delete task');
      }
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Error deleting task');
    }
  };

  // Checklist operations
  const handleAddChecklistItem = async () => {
    if (!newChecklistItem.trim() || !selectedTask) return;
    
    const newItem = {
      id: `temp-${Date.now()}`,
      text: newChecklistItem.trim(),
      completed: false,
    };
    
    const updatedChecklist = [...(selectedTask.checklist || []), newItem];
    
    // Optimistic update
    setSelectedTask(prev => ({ ...prev, checklist: updatedChecklist }));
    setNewChecklistItem('');
    
    // Send to backend
    try {
      const response = await fetch(`${API_URL}/api/tasks/${selectedTask.id}/checklist`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ text: newItem.text }),
      });
      
      if (response.ok) {
        const updated = await response.json();
        setSelectedTask(updated);
        setTasks(prev => prev.map(t => t.id === selectedTask.id ? updated : t));
      }
    } catch (error) {
      console.error('Error adding checklist item:', error);
    }
  };

  const handleToggleChecklistItem = async (itemId) => {
    if (!selectedTask) return;
    
    const item = selectedTask.checklist?.find(i => i.id === itemId);
    if (!item) return;
    
    // Optimistic update
    const updatedChecklist = selectedTask.checklist.map(i =>
      i.id === itemId ? { ...i, completed: !i.completed } : i
    );
    setSelectedTask(prev => ({ ...prev, checklist: updatedChecklist }));
    
    try {
      const response = await fetch(`${API_URL}/api/tasks/${selectedTask.id}/checklist/${itemId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ completed: !item.completed }),
      });
      
      if (response.ok) {
        const updated = await response.json();
        setSelectedTask(updated);
        setTasks(prev => prev.map(t => t.id === selectedTask.id ? updated : t));
      }
    } catch (error) {
      console.error('Error toggling checklist item:', error);
    }
  };

  const handleDeleteChecklistItem = async (itemId) => {
    if (!selectedTask) return;
    
    // Optimistic update
    const updatedChecklist = selectedTask.checklist.filter(i => i.id !== itemId);
    setSelectedTask(prev => ({ ...prev, checklist: updatedChecklist }));
    
    try {
      const response = await fetch(`${API_URL}/api/tasks/${selectedTask.id}/checklist/${itemId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      
      if (response.ok) {
        const updated = await response.json();
        setSelectedTask(updated);
        setTasks(prev => prev.map(t => t.id === selectedTask.id ? updated : t));
      }
    } catch (error) {
      console.error('Error deleting checklist item:', error);
    }
  };

  // Drag and drop handlers for Kanban
  const handleDragStart = (e, task) => {
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    if (!draggedTask || draggedTask.status === newStatus) {
      setDraggedTask(null);
      return;
    }
    
    await handleStatusChange(draggedTask.id, newStatus);
    setDraggedTask(null);
  };

  const handleDragEnd = () => {
    setDraggedTask(null);
  };

  // Reset form
  const resetNewTaskForm = () => {
    setNewTask({
      title: '',
      description: '',
      task_type: 'self',
      assigned_to_id: '',
      collaborators: [],
      priority: 'Medium',
      due_date: '',
      category: '',
      tags: [],
    });
  };

  // Filter tasks
  const filteredTasks = tasks.filter(task => {
    const matchesSearch = task.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          task.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || task.status === filterStatus;
    const matchesPriority = filterPriority === 'all' || task.priority === filterPriority;
    return matchesSearch && matchesStatus && matchesPriority;
  });

  // Group tasks by status for Kanban
  const tasksByStatus = KANBAN_COLUMNS.reduce((acc, status) => {
    acc[status] = filteredTasks.filter(t => t.status === status);
    return acc;
  }, {});

  // Get initials
  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Check if overdue
  const isOverdue = (task) => {
    if (!task.due_date || task.status === 'Completed' || task.status === 'Cancelled') return false;
    return new Date(task.due_date) < new Date();
  };

  // Task Card Component
  const TaskCard = ({ task, isDragging }) => {
    const overdue = isOverdue(task);
    const completedItems = task.checklist?.filter(i => i.completed).length || 0;
    const totalItems = task.checklist?.length || 0;
    
    return (
      <motion.div
        layout
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: isDragging ? 0.5 : 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9 }}
        draggable
        onDragStart={(e) => handleDragStart(e, task)}
        onDragEnd={handleDragEnd}
        onClick={() => {
          setSelectedTask(task);
          setIsDetailDialogOpen(true);
        }}
        className={`p-3 bg-card rounded-lg border border-border shadow-sm cursor-pointer
          hover:shadow-md hover:border-primary/20 transition-all group
          ${isDragging ? 'ring-2 ring-primary' : ''}`}
        data-testid={`task-card-${task.id}`}
      >
        {/* Task Type Badge */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5">
            {TASK_TYPES[task.task_type] && (
              <span className={`${TASK_TYPES[task.task_type].color}`}>
                {React.createElement(TASK_TYPES[task.task_type].icon, { className: 'w-3 h-3' })}
              </span>
            )}
            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${PRIORITIES[task.priority]?.color}`}>
              {task.priority}
            </span>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover:opacity-100">
                <MoreHorizontal className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setSelectedTask(task); setIsDetailDialogOpen(true); }}>
                <Edit2 className="w-3.5 h-3.5 mr-2" /> Edit
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                className="text-red-600" 
                onClick={(e) => { e.stopPropagation(); handleDeleteTask(task.id); }}
              >
                <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        
        {/* Title */}
        <h4 className="font-medium text-sm mb-2 line-clamp-2">{task.title}</h4>
        
        {/* Description preview */}
        {task.description && (
          <p className="text-xs text-muted-foreground mb-2 line-clamp-2">{task.description}</p>
        )}
        
        {/* Checklist progress */}
        {totalItems > 0 && (
          <div className="flex items-center gap-2 mb-2">
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <CheckSquare className="w-3 h-3" />
              <span>{completedItems}/{totalItems}</span>
            </div>
            <div className="flex-1 h-1 bg-muted rounded-full overflow-hidden">
              <div 
                className="h-full bg-green-500 transition-all"
                style={{ width: `${(completedItems / totalItems) * 100}%` }}
              />
            </div>
          </div>
        )}
        
        {/* Footer */}
        <div className="flex items-center justify-between mt-2 pt-2 border-t border-border">
          {/* Due date */}
          {task.due_date && (
            <div className={`flex items-center gap-1 text-xs ${overdue ? 'text-red-500' : 'text-muted-foreground'}`}>
              <Calendar className="w-3 h-3" />
              <span>{formatDate(task.due_date)}</span>
            </div>
          )}
          
          {/* Assignee */}
          <div className="flex -space-x-1">
            {task.assigned_to_name && (
              <Avatar className="h-5 w-5 border border-background">
                <AvatarFallback className="text-[9px] bg-primary/10">
                  {getInitials(task.assigned_to_name)}
                </AvatarFallback>
              </Avatar>
            )}
            {task.collaborators?.slice(0, 2).map((c, i) => (
              <Avatar key={i} className="h-5 w-5 border border-background">
                <AvatarFallback className="text-[9px] bg-purple-100">
                  {getInitials(c.name)}
                </AvatarFallback>
              </Avatar>
            ))}
            {task.collaborators?.length > 2 && (
              <div className="h-5 w-5 rounded-full bg-muted flex items-center justify-center text-[9px] border border-background">
                +{task.collaborators.length - 2}
              </div>
            )}
          </div>
        </div>
      </motion.div>
    );
  };

  // Kanban Column Component
  const KanbanColumn = ({ status, tasks }) => {
    const statusConfig = STATUSES[status];
    const isDropTarget = draggedTask && draggedTask.status !== status;
    
    return (
      <div
        className={`flex-1 min-w-[280px] max-w-[350px] bg-muted/30 rounded-lg p-3 transition-all
          ${isDropTarget ? 'ring-2 ring-primary ring-dashed' : ''}`}
        onDragOver={handleDragOver}
        onDrop={(e) => handleDrop(e, status)}
        data-testid={`kanban-column-${status.replace(/\s/g, '-').toLowerCase()}`}
      >
        {/* Column Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${statusConfig.dotColor}`} />
            <h3 className="font-semibold text-sm">{status}</h3>
            <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded-full">
              {tasks.length}
            </span>
          </div>
        </div>
        
        {/* Tasks */}
        <div className="space-y-2 min-h-[200px] max-h-[480px] overflow-y-auto">
          <AnimatePresence mode="popLayout">
            {tasks.map(task => (
              <TaskCard 
                key={task.id} 
                task={task} 
                isDragging={draggedTask?.id === task.id}
              />
            ))}
          </AnimatePresence>
          
          {tasks.length === 0 && (
            <div className="flex items-center justify-center h-[100px] text-muted-foreground text-sm border-2 border-dashed border-muted rounded-lg">
              No tasks
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22 }}
      className="space-y-6"
      data-testid="tasks-page"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Tasks</h1>
          <p className="text-muted-foreground mt-1">Manage your work and collaborate with your team</p>
        </div>
        <Button onClick={() => setIsCreateDialogOpen(true)} data-testid="create-task-button">
          <Plus className="w-4 h-4 mr-2" />
          New Task
        </Button>
      </div>

      {/* Toolbar */}
      <Card className="p-4">
        <div className="flex items-center gap-4 flex-wrap">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search tasks..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="task-search-input"
            />
          </div>
          
          {/* Filters */}
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[140px]" data-testid="filter-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {Object.keys(STATUSES).map(status => (
                <SelectItem key={status} value={status}>{status}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={filterPriority} onValueChange={setFilterPriority}>
            <SelectTrigger className="w-[140px]" data-testid="filter-priority">
              <SelectValue placeholder="Priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Priority</SelectItem>
              {Object.keys(PRIORITIES).map(priority => (
                <SelectItem key={priority} value={priority}>{priority}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* View Toggle */}
          <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
            <Button
              variant={viewMode === 'kanban' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('kanban')}
              data-testid="view-kanban"
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              data-testid="view-list"
            >
              <List className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </Card>

      {/* Main Content */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : viewMode === 'kanban' ? (
        /* Kanban View */
        <div className="flex gap-4 overflow-x-auto pb-4">
          {KANBAN_COLUMNS.map(status => (
            <KanbanColumn 
              key={status} 
              status={status} 
              tasks={tasksByStatus[status] || []}
            />
          ))}
        </div>
      ) : (
        /* List View */
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-3 font-medium text-sm">Task</th>
                  <th className="text-left p-3 font-medium text-sm w-32">Status</th>
                  <th className="text-left p-3 font-medium text-sm w-28">Priority</th>
                  <th className="text-left p-3 font-medium text-sm w-28">Due Date</th>
                  <th className="text-left p-3 font-medium text-sm w-32">Assignee</th>
                  <th className="w-12"></th>
                </tr>
              </thead>
              <tbody>
                {filteredTasks.map((task, idx) => (
                  <motion.tr
                    key={task.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: idx * 0.03 }}
                    className="border-t border-border hover:bg-accent/50 cursor-pointer"
                    onClick={() => { setSelectedTask(task); setIsDetailDialogOpen(true); }}
                    data-testid={`task-row-${task.id}`}
                  >
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        {TASK_TYPES[task.task_type] && (
                          <span className={TASK_TYPES[task.task_type].color}>
                            {React.createElement(TASK_TYPES[task.task_type].icon, { className: 'w-4 h-4' })}
                          </span>
                        )}
                        <span className="font-medium">{task.title}</span>
                        {task.checklist?.length > 0 && (
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <CheckSquare className="w-3 h-3" />
                            {task.checklist.filter(i => i.completed).length}/{task.checklist.length}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-3" onClick={(e) => e.stopPropagation()}>
                      <Select value={task.status} onValueChange={(v) => handleStatusChange(task.id, v)}>
                        <SelectTrigger className="h-8 w-full border-0 bg-transparent hover:bg-accent">
                          <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${STATUSES[task.status]?.dotColor}`} />
                            <span className="text-sm">{task.status}</span>
                          </div>
                        </SelectTrigger>
                        <SelectContent>
                          {Object.keys(STATUSES).map(status => (
                            <SelectItem key={status} value={status}>
                              <div className="flex items-center gap-2">
                                <span className={`w-2 h-2 rounded-full ${STATUSES[status].dotColor}`} />
                                {status}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="p-3">
                      <span className={`text-xs px-2 py-1 rounded ${PRIORITIES[task.priority]?.color}`}>
                        {task.priority}
                      </span>
                    </td>
                    <td className="p-3">
                      {task.due_date && (
                        <span className={`text-sm ${isOverdue(task) ? 'text-red-500' : 'text-muted-foreground'}`}>
                          {formatDate(task.due_date)}
                        </span>
                      )}
                    </td>
                    <td className="p-3">
                      {task.assigned_to_name && (
                        <div className="flex items-center gap-2">
                          <Avatar className="h-6 w-6">
                            <AvatarFallback className="text-xs">{getInitials(task.assigned_to_name)}</AvatarFallback>
                          </Avatar>
                          <span className="text-sm truncate max-w-[100px]">{task.assigned_to_name}</span>
                        </div>
                      )}
                    </td>
                    <td className="p-3" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => { setSelectedTask(task); setIsDetailDialogOpen(true); }}>
                            <Edit2 className="w-4 h-4 mr-2" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-red-600" onClick={() => handleDeleteTask(task.id)}>
                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
            
            {filteredTasks.length === 0 && (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No tasks found</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => setIsCreateDialogOpen(true)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create your first task
                </Button>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Create Task Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="create-task-dialog">
          <DialogHeader>
            <DialogTitle>Create New Task</DialogTitle>
            <DialogDescription>Add a new task to your list</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Title */}
            <div>
              <label className="text-sm font-medium mb-1.5 block">Title *</label>
              <Input
                value={newTask.title}
                onChange={(e) => setNewTask(prev => ({ ...prev, title: e.target.value }))}
                placeholder="What needs to be done?"
                data-testid="new-task-title"
              />
            </div>

            {/* Description */}
            <div>
              <label className="text-sm font-medium mb-1.5 block">Description</label>
              <Textarea
                value={newTask.description}
                onChange={(e) => setNewTask(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Add more details..."
                rows={3}
                data-testid="new-task-description"
              />
            </div>

            {/* Task Type */}
            <div>
              <label className="text-sm font-medium mb-1.5 block">Task Type</label>
              <Select 
                value={newTask.task_type} 
                onValueChange={(v) => setNewTask(prev => ({ ...prev, task_type: v, assigned_to_id: '', collaborators: [] }))}
              >
                <SelectTrigger data-testid="new-task-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="self">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-blue-500" />
                      Personal - Just for me
                    </div>
                  </SelectItem>
                  <SelectItem value="assigned">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-green-500" />
                      Assign to someone
                    </div>
                  </SelectItem>
                  <SelectItem value="collaboration">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-purple-500" />
                      Team collaboration
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Assignee (for assigned tasks) */}
            {newTask.task_type === 'assigned' && (
              <div>
                <label className="text-sm font-medium mb-1.5 block">Assign To</label>
                <Select 
                  value={newTask.assigned_to_id} 
                  onValueChange={(v) => setNewTask(prev => ({ ...prev, assigned_to_id: v }))}
                >
                  <SelectTrigger data-testid="new-task-assignee">
                    <SelectValue placeholder="Select team member" />
                  </SelectTrigger>
                  <SelectContent>
                    {teamMembers.map(member => (
                      <SelectItem key={member.user_id} value={member.user_id}>
                        <div className="flex items-center gap-2">
                          <Avatar className="h-5 w-5">
                            <AvatarFallback className="text-xs">{getInitials(member.name)}</AvatarFallback>
                          </Avatar>
                          {member.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Collaborators (for collaboration tasks) */}
            {newTask.task_type === 'collaboration' && (
              <div>
                <label className="text-sm font-medium mb-1.5 block">Team Members</label>
                <div className="border rounded-lg p-3 space-y-2 max-h-40 overflow-y-auto">
                  {teamMembers.map(member => (
                    <div key={member.user_id} className="flex items-center gap-2">
                      <Checkbox
                        checked={newTask.collaborators.includes(member.user_id)}
                        onCheckedChange={(checked) => {
                          setNewTask(prev => ({
                            ...prev,
                            collaborators: checked 
                              ? [...prev.collaborators, member.user_id]
                              : prev.collaborators.filter(id => id !== member.user_id)
                          }));
                        }}
                      />
                      <Avatar className="h-6 w-6">
                        <AvatarFallback className="text-xs">{getInitials(member.name)}</AvatarFallback>
                      </Avatar>
                      <span className="text-sm">{member.name}</span>
                    </div>
                  ))}
                  {teamMembers.length === 0 && (
                    <p className="text-sm text-muted-foreground">No team members available</p>
                  )}
                </div>
              </div>
            )}

            {/* Priority & Due Date */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1.5 block">Priority</label>
                <Select value={newTask.priority} onValueChange={(v) => setNewTask(prev => ({ ...prev, priority: v }))}>
                  <SelectTrigger data-testid="new-task-priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.keys(PRIORITIES).map(priority => (
                      <SelectItem key={priority} value={priority}>{priority}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">Due Date</label>
                <Input
                  type="date"
                  value={newTask.due_date}
                  onChange={(e) => setNewTask(prev => ({ ...prev, due_date: e.target.value }))}
                  data-testid="new-task-duedate"
                />
              </div>
            </div>

            {/* Category */}
            <div>
              <label className="text-sm font-medium mb-1.5 block">Category (optional)</label>
              <Input
                value={newTask.category}
                onChange={(e) => setNewTask(prev => ({ ...prev, category: e.target.value }))}
                placeholder="e.g., Development, Marketing"
                data-testid="new-task-category"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateTask} disabled={!newTask.title.trim()} data-testid="confirm-create-task">
              <Plus className="w-4 h-4 mr-2" />
              Create Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task Detail Dialog */}
      <Dialog open={isDetailDialogOpen} onOpenChange={setIsDetailDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="task-detail-dialog">
          {selectedTask && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1 pr-4">
                    <DialogTitle className="text-xl">{selectedTask.title}</DialogTitle>
                    {selectedTask.description && (
                      <DialogDescription className="mt-2">{selectedTask.description}</DialogDescription>
                    )}
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-6 py-4">
                {/* Status & Priority Row */}
                <div className="flex flex-wrap gap-4">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">Status</label>
                    <Select value={selectedTask.status} onValueChange={(v) => handleStatusChange(selectedTask.id, v)}>
                      <SelectTrigger className="w-[150px]" data-testid="detail-status">
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${STATUSES[selectedTask.status]?.dotColor}`} />
                          {selectedTask.status}
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        {Object.keys(STATUSES).map(status => (
                          <SelectItem key={status} value={status}>
                            <div className="flex items-center gap-2">
                              <span className={`w-2 h-2 rounded-full ${STATUSES[status].dotColor}`} />
                              {status}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">Priority</label>
                    <div className={`px-3 py-2 rounded-md text-sm font-medium ${PRIORITIES[selectedTask.priority]?.color}`}>
                      {selectedTask.priority}
                    </div>
                  </div>
                  
                  {selectedTask.due_date && (
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1 block">Due Date</label>
                      <div className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm ${isOverdue(selectedTask) ? 'bg-red-50 text-red-700' : 'bg-muted'}`}>
                        <Calendar className="w-4 h-4" />
                        {formatDate(selectedTask.due_date)}
                      </div>
                    </div>
                  )}
                </div>

                {/* Assignees */}
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-2 block">
                    {selectedTask.task_type === 'collaboration' ? 'Team Members' : 'Assigned To'}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {selectedTask.assigned_to_name && (
                      <div className="flex items-center gap-2 bg-accent px-3 py-1.5 rounded-full">
                        <Avatar className="h-5 w-5">
                          <AvatarFallback className="text-xs">{getInitials(selectedTask.assigned_to_name)}</AvatarFallback>
                        </Avatar>
                        <span className="text-sm">{selectedTask.assigned_to_name}</span>
                      </div>
                    )}
                    {selectedTask.collaborators?.map((c, i) => (
                      <div key={i} className="flex items-center gap-2 bg-purple-50 px-3 py-1.5 rounded-full">
                        <Avatar className="h-5 w-5">
                          <AvatarFallback className="text-xs bg-purple-100">{getInitials(c.name)}</AvatarFallback>
                        </Avatar>
                        <span className="text-sm">{c.name}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Checklist Section */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium flex items-center gap-2">
                      <CheckSquare className="w-4 h-4" />
                      Checklist
                      {selectedTask.checklist?.length > 0 && (
                        <span className="text-xs text-muted-foreground">
                          ({selectedTask.checklist.filter(i => i.completed).length}/{selectedTask.checklist.length})
                        </span>
                      )}
                    </label>
                  </div>
                  
                  {/* Checklist Items */}
                  <div className="space-y-2 mb-3">
                    {selectedTask.checklist?.map((item) => (
                      <div 
                        key={item.id} 
                        className="flex items-center gap-3 group p-2 rounded-md hover:bg-accent/50"
                      >
                        <button
                          onClick={() => handleToggleChecklistItem(item.id)}
                          className="flex-shrink-0"
                        >
                          {item.completed ? (
                            <CheckSquare className="w-5 h-5 text-green-500" />
                          ) : (
                            <Square className="w-5 h-5 text-muted-foreground" />
                          )}
                        </button>
                        <span className={`flex-1 text-sm ${item.completed ? 'line-through text-muted-foreground' : ''}`}>
                          {item.text}
                        </span>
                        <button
                          onClick={() => handleDeleteChecklistItem(item.id)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-red-50 rounded"
                        >
                          <X className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    ))}
                  </div>
                  
                  {/* Add Checklist Item */}
                  <div className="flex gap-2">
                    <Input
                      value={newChecklistItem}
                      onChange={(e) => setNewChecklistItem(e.target.value)}
                      placeholder="Add a sub-task..."
                      onKeyDown={(e) => e.key === 'Enter' && handleAddChecklistItem()}
                      data-testid="new-checklist-item"
                    />
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={handleAddChecklistItem}
                      disabled={!newChecklistItem.trim()}
                      data-testid="add-checklist-item"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Collaborators */}
                {selectedTask.task_type === 'collaboration' && (
                  <CollaboratorsList task={selectedTask} employees={teamMembers} />
                )}

                {/* Comments */}
                <TaskComments
                  taskId={selectedTask.id}
                  task={selectedTask}
                  collaborators={selectedTask.collaborators || []}
                  employees={teamMembers}
                />

                {/* Meta info */}
                <div className="border-t pt-4 text-xs text-muted-foreground space-y-1">
                  {selectedTask.created_by_name && (
                    <p>Created by {selectedTask.created_by_name}</p>
                  )}
                  {selectedTask.created_at && (
                    <p>Created on {new Date(selectedTask.created_at).toLocaleDateString()}</p>
                  )}
                </div>
              </div>

              <DialogFooter className="gap-2">
                <Button 
                  variant="outline" 
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => handleDeleteTask(selectedTask.id)}
                  data-testid="delete-task-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
                <Button onClick={() => setIsDetailDialogOpen(false)} data-testid="close-detail">
                  Close
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

export default Tasks;
